/**
 * useSyncToStorage — syncs AppContext values back to sessionStorage on change.
 *
 * This is the ONLY place where context → sessionStorage writes should happen.
 * Components should never call sessionStorage.setItem() for context values directly.
 *
 * Usage: call once in App.js or AppProvider.
 *
 *   import { useSyncToStorage } from '../hooks/useSyncToStorage';
 *   useSyncToStorage();
 */
import { useEffect, useRef } from 'react';

import { useAppContext } from '../../app/provider/useAppContext';
import { STORAGE_KEYS, syncToStorage } from '../utils/storage';

export function useSyncToStorage(): void {
  const [ctx] = useAppContext();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the initial mount — values were hydrated FROM storage already
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Persist context fields that need to survive page refresh
    syncToStorage(STORAGE_KEYS.CURRENCY, ctx.currency);
    syncToStorage(STORAGE_KEYS.COUNTRY, ctx.country);
    syncToStorage(STORAGE_KEYS.LOCALE, ctx.locale);
    syncToStorage(STORAGE_KEYS.CHANNEL_ID, ctx.channelId);
    syncToStorage(STORAGE_KEYS.CHANNEL_NAME, ctx.channelName);
    syncToStorage(STORAGE_KEYS.STORE_KEY, ctx.storeKey);
    syncToStorage(STORAGE_KEYS.STORE_NAME, ctx.storeName);
    syncToStorage(STORAGE_KEYS.CUSTOMER_GROUP_ID, ctx.customerGroupId);
    syncToStorage(STORAGE_KEYS.CUSTOMER_GROUP_NAME, ctx.customerGroupName);
  }, [
    ctx.currency,
    ctx.country,
    ctx.locale,
    ctx.channelId,
    ctx.channelName,
    ctx.storeKey,
    ctx.storeName,
    ctx.customerGroupId,
    ctx.customerGroupName,
  ]);
}
